/**
 * ConfidenceMeter — small horizontal bar that visualises a 0–1 confidence
 * score. Used by the rule cards and the rule detail panel. Color shifts
 * from amber (low) through teal (mid) to brand-green (high) so the same
 * meter shape works for DO and DON'T rules without conflicting with the
 * polarity-driven card border colors.
 */

import { clsx } from 'clsx';

interface ConfidenceMeterProps {
  /** 0..1 */
  value: number;
  /** Show the numeric value next to the bar (defaults to true) */
  showLabel?: boolean;
  /** Compact variant for inline use in rule cards */
  size?: 'sm' | 'md';
}

const segmentColor = (v: number): string => {
  if (v >= 0.8) return 'bg-emerald-500';
  if (v >= 0.6) return 'bg-teal-500';
  if (v >= 0.4) return 'bg-amber-500';
  return 'bg-rose-400';
};

export function ConfidenceMeter({
  value,
  showLabel = true,
  size = 'md',
}: ConfidenceMeterProps) {
  const clamped = Math.max(0, Math.min(1, value));
  const pct = Math.round(clamped * 100);
  const barH = size === 'sm' ? 'h-1.5' : 'h-2';

  return (
    <div className="flex items-center gap-2 min-w-0">
      <div
        className={clsx(
          'flex-1 rounded-full bg-slate-100 overflow-hidden',
          barH,
        )}
        title={`Confidence: ${pct}%`}
      >
        <div
          className={clsx('h-full transition-all', segmentColor(clamped))}
          style={{ width: `${pct}%` }}
        />
      </div>
      {showLabel && (
        <span className="text-[11px] tabular-nums text-slate-600 shrink-0 w-9 text-right">
          {clamped.toFixed(2)}
        </span>
      )}
    </div>
  );
}
